/**
 * Hook to link affiliate referrals on user authentication
 * 
 * This hook checks for affiliate referral codes stored in localStorage
 * (set by the /invite/:slug page) and links them to the user's account
 * when they authenticate.
 * 
 * Flow:
 * 1. User visits /invite/:slug or /r/:slug (redirect)
 * 2. Frontend stores ref_code and ref_campaign in localStorage
 * 3. User signs up/logs in
 * 4. This hook detects auth and calls /api/affiliate-referrals/link
 * 5. Backend creates referral record linking user → affiliate
 */

import { useEffect, useRef } from "react";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { API_BASE_URL } from "@/backend/config";

const REF_CODE_KEY = "ref_code";
const REF_CAMPAIGN_KEY = "ref_campaign";
const AFFILIATE_LINKED_KEY = "nordex_affiliate_linked";

interface AffiliateReferralData {
  refCode: string;
  campaignSlug?: string;
}

/**
 * Hook to automatically link affiliate referrals on first login
 * Should be mounted at App level to ensure it runs for all authenticated users
 */
export function useAffiliateReferralLinking() {
  const { userId, isAuthenticated, isReady } = useUnifiedAuth();
  const linkingInProgress = useRef(false);

  useEffect(() => {
    // Wait for auth to be ready and user to be authenticated
    if (!isReady || !isAuthenticated || !userId) return;

    // Prevent duplicate calls
    if (linkingInProgress.current) return;

    // Check if already linked for this user (prevents re-linking on subsequent logins)
    const linkedKey = `${AFFILIATE_LINKED_KEY}_${userId}`;
    if (localStorage.getItem(linkedKey)) {
      console.debug("[AffiliateReferral] Already linked for user, skipping");
      return;
    }

    // Get stored affiliate referral data
    const referralData = getStoredAffiliateReferral();
    if (!referralData) {
      console.debug("[AffiliateReferral] No referral data found, skipping");
      return;
    }

    // Mark as in progress to prevent race conditions
    linkingInProgress.current = true;

    // Call the backend to link the referral
    console.log("[AffiliateReferral] Linking referral:", {
      userId,
      refCode: referralData.refCode,
      campaign: referralData.campaignSlug,
    });

    fetch(`${API_BASE_URL}/api/affiliate-referrals/link?user_id=${userId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ref_code: referralData.refCode,
        campaign_slug: referralData.campaignSlug || undefined,
      }),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        
        if (response.ok && data.success) {
          console.log("[AffiliateReferral] ✅ Successfully linked:", data.message);
          // Mark as linked for this user (prevents re-attempts)
          localStorage.setItem(linkedKey, "true");
          // Clear the stored referral data
          clearStoredAffiliateReferral();
        } else if (response.ok && !data.success) {
          // User already referred - still mark as processed
          console.log("[AffiliateReferral] User already referred:", data.message);
          localStorage.setItem(linkedKey, "true");
          clearStoredAffiliateReferral();
        } else {
          // API error - don't mark as linked so we can retry
          console.warn("[AffiliateReferral] Failed to link:", data.message || response.statusText);
        }
      })
      .catch((error) => {
        console.error("[AffiliateReferral] Error linking:", error);
      })
      .finally(() => {
        linkingInProgress.current = false;
      });
  }, [isReady, isAuthenticated, userId]);
}

/**
 * Get stored affiliate referral data from localStorage
 */
function getStoredAffiliateReferral(): AffiliateReferralData | null {
  try {
    const refCode = localStorage.getItem(REF_CODE_KEY);
    if (!refCode) return null;

    const campaignSlug = localStorage.getItem(REF_CAMPAIGN_KEY) || undefined;

    return {
      refCode,
      campaignSlug,
    };
  } catch (error) {
    console.error("[AffiliateReferral] Error reading localStorage:", error);
    return null;
  }
}

/**
 * Clear stored affiliate referral data
 */
function clearStoredAffiliateReferral() {
  try {
    localStorage.removeItem(REF_CODE_KEY);
    localStorage.removeItem(REF_CAMPAIGN_KEY);
    console.debug("[AffiliateReferral] Cleared stored referral data");
  } catch (error) {
    console.error("[AffiliateReferral] Error clearing localStorage:", error);
  }
}

/**
 * Utility to manually store affiliate referral data
 * (Can be called from invite page if needed)
 */
export function storeAffiliateReferral(refCode: string, campaignSlug?: string) {
  try {
    localStorage.setItem(REF_CODE_KEY, refCode);
    if (campaignSlug) {
      localStorage.setItem(REF_CAMPAIGN_KEY, campaignSlug);
    }
    console.log("[AffiliateReferral] Stored referral:", { refCode, campaignSlug });
  } catch (error) {
    console.error("[AffiliateReferral] Error storing referral:", error);
  }
}

